import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock3, MapPin, ArrowRight, Flag } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingBlock } from "@/components/LoadingState";
import { useMatches, useOfficialMatches } from "@/queries/matches";
import {
  describeMatchStart,
  getMatchVenue,
  parseMatchDate,
  toDateKey,
  getMatchTeams,
} from "@/utils/matchFormatting";
import { asArray } from "@/lib/asArray";
import PageTutorial from "@/components/tutorial/PageTutorial";
import TeamLogo from "@/components/TeamLogo";
import { getNativePlatform } from "@/lib/runtime";
import { NativeMatchPickerSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";

function isManualCancelled(match) {
  return String(match?.manual_status || "").trim().toLowerCase() === "cancelled";
}

function getOfficialMatchPill(match, isToday) {
  if (isManualCancelled(match)) {
    return { label: "Inställd", variant: "destructive" };
  }
  if (isToday) {
    return { label: "Idag", variant: "secondary" };
  }
  return { label: "Kommande", variant: "outline" };
}

export default function ProtocolMatchesPicker({ onOpenOfficial, onOpenAll }) {
  const { user } = useAuth();
  const isNativeAndroid = getNativePlatform() === "android";

  const { data: matches = [] } = useMatches({
    staleTime: Infinity,
    enabled: !!user,
  });

  const consumedOfficialIds = React.useMemo(() => {
    const list = asArray(matches);
    const ids = list
      .map((match) => match?.official_match_id || match?.officialMatchId)
      .filter(Boolean)
      .map((id) => String(id));
    return new Set(ids);
  }, [matches]);

  const {
    data: officialRaw = [],
    isLoading,
    error,
  } = useOfficialMatches({
    enabled: !!user,
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const normalizedOfficial = React.useMemo(
    () =>
      officialRaw
        .map((m) => ({
          ...m,
          idStr: String(m?.id ?? m?._id ?? ""),
          __parsedDate: parseMatchDate(m),
        }))
        .sort((a, b) => {
          const aTime = a.__parsedDate?.getTime() ?? Infinity;
          const bTime = b.__parsedDate?.getTime() ?? Infinity;
          return aTime - bTime;
        }),
    [officialRaw]
  );

  const official = React.useMemo(() => {
    const today = new Date();
    const cutoff = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 21, 30, 0, 0);
    const effectiveBaseDate = today >= cutoff
      ? new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
      : new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return normalizedOfficial
      .filter((match) => {
        const rawId = match?.id ?? match?._id;
        if (!rawId) return true;
        const officialId = String(rawId);
        if (consumedOfficialIds.has(officialId)) return false;
        return !match?.consumedByCurrentUser;
      })
      .filter((m) => {
        const dt = m.__parsedDate;
        if (!dt) return true;
        const matchDay = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
        return matchDay >= effectiveBaseDate;
      });
  }, [normalizedOfficial, consumedOfficialIds]);

  const nextDateKey = React.useMemo(() => {
    const firstWithDate = official.find((m) => m.__parsedDate);
    return toDateKey(firstWithDate?.__parsedDate ?? null);
  }, [official]);

  const featuredMatches = React.useMemo(() => {
    if (!nextDateKey) return official;
    return official.filter((m) => toDateKey(m.__parsedDate) === nextDateKey);
  }, [official, nextDateKey]);

  if (isLoading) {
    return isNativeAndroid
      ? <NativeMatchPickerSkeleton />
      : <LoadingBlock text="Hämtar kommande matcher" />;
  }

  if (error) {
    return <p className="text-sm text-red-600">Kunde inte hämta officiella matcher.</p>;
  }

  if (!featuredMatches.length) {
    if (isNativeAndroid) {
      return (
        <div className="native-v3-protocol-empty">
          <Flag />
          <strong>Inga kommande matcher</strong>
          <span>Det finns ingen officiell match att starta protokoll från just nu.</span>
          <button type="button" onClick={onOpenAll}>Visa hela matchlistan</button>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Inga kommande matcher att starta protokoll från just nu.</p>
        <Button type="button" variant="outline" onClick={onOpenAll}>
          Visa hela matchlistan
        </Button>
      </div>
    );
  }

  if (isNativeAndroid) {
    return (
      <div id="upcomingMatches" className="native-v3-protocol-picker">
        <PageTutorial pageName="matches" autoStart={!isLoading && !error && featuredMatches.length > 0} />
        <div className="native-v3-protocol-picker-heading">
          <span>Officiella matcher</span>
          <h3>Välj match</h3>
          <p>Öppna matchkortet och kontrollera laguppställningen innan protokollet startas.</p>
        </div>
        <div id="featuredMatches" className="native-v3-protocol-match-list">
          {featuredMatches.map((match, index) => {
            const { dateLabel, timeLabel, isToday } = describeMatchStart(match);
            const venue = getMatchVenue(match);
            const { home, away } = getMatchTeams(match);
            const pill = getOfficialMatchPill(match, isToday);

            return (
              <button
                type="button"
                key={match.idStr}
                id={index === 0 ? "featuredMatchesPrimary" : undefined}
                className="native-v3-protocol-match"
                data-status={pill.variant}
                onClick={() => onOpenOfficial(match.idStr)}
              >
                <span className="native-v3-protocol-match-status">{pill.label}</span>
                <span className="native-v3-protocol-match-teams">
                  <span>
                    <TeamLogo teamName={home} size={44} />
                    <strong>{home}</strong>
                  </span>
                  <small>VS</small>
                  <span>
                    <TeamLogo teamName={away} size={44} />
                    <strong>{away}</strong>
                  </span>
                </span>
                <span className="native-v3-protocol-match-meta">
                  <span><CalendarDays />{dateLabel}</span>
                  {timeLabel ? <span><Clock3 />{timeLabel}</span> : null}
                  {venue ? <span><MapPin />{venue}</span> : null}
                </span>
                <span className="native-v3-protocol-match-action">
                  Se matchkort <ArrowRight />
                </span>
              </button>
            );
          })}
        </div>
        <button id="matchFilters" type="button" className="native-v3-protocol-all" onClick={onOpenAll}>
          Visa hela matchlistan <ArrowRight />
        </button>
      </div>
    );
  }

  return (
    <div id="upcomingMatches" className="space-y-3">
      <PageTutorial pageName="matches" autoStart={!isLoading && !error && featuredMatches.length > 0} />
      <div id="featuredMatches" className="space-y-3">
      {featuredMatches.map((match, index) => {
        const { dateLabel, timeLabel, isToday } = describeMatchStart(match);
        const venue = getMatchVenue(match);
        const { home, away } = getMatchTeams(match);
        const pill = getOfficialMatchPill(match, isToday);
        return (
          <button
            type="button"
            key={match.idStr}
            id={index === 0 ? "featuredMatchesPrimary" : undefined}
            onClick={() => onOpenOfficial(match.idStr)}
            className="w-full rounded-xl border bg-card p-4 text-left transition hover:border-primary/60"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  {home}
                  <span className="text-muted-foreground"> vs </span>
                  {away}
                </h3>
                <div className="mt-2 space-y-1.5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    <span>
                      {dateLabel}
                    </span>
                  </div>
                  {timeLabel ? (
                    <div className="flex items-center gap-2">
                      <Clock3 className="h-4 w-4" />
                      <span>{timeLabel}</span>
                    </div>
                  ) : null}
                  {venue ? (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{venue}</span>
                    </div>
                  ) : null}
                </div>
              </div>
              <Badge className="native-v3-status-badge" variant={pill.variant}>
                {pill.label}
              </Badge>
            </div>
            <div className="mt-3 flex items-center gap-1 text-sm font-medium text-primary">
              <span>Öppna matchkort</span>
              <ArrowRight className="h-4 w-4" />
            </div>
          </button>
        );
      })}
      </div>

      <div className="pt-1">
        <Button id="matchFilters" type="button" variant="ghost" className="w-full" onClick={onOpenAll}>
          Visa hela matchlistan
        </Button>
      </div>
    </div>
  );
}
